import { apiService } from './api'
import { AuthResponse, LoginData, User, Language, AccessibleFeature } from '../types/auth'

interface NewTenantRegistrationData {
  email: string
  name: string
  companyName: string
  department?: string
  position?: string
  sendTermsEmail?: boolean
}

// MFA関連の型定義
export interface MfaStatus {
  enabled: boolean
  verifiedAt: string | null
  remainingBackupCodes: number
}

export interface MfaSetupResponse {
  secret: string
  qrCodeUrl: string
  otpAuthUrl: string
}

export interface MfaEnableResponse {
  message: string
  backupCodes: string[]
}

export interface MfaLoginResponse {
  requiresMfa: boolean
  mfaSessionToken: string
  message: string
}

export interface MfaVerifyResponse {
  message: string
  token: string
  user: User
  remainingBackupCodes?: number
  warning?: string
}

export class AuthService {
  // 新規テナント・管理者登録
  async registerNewTenant(registrationData: NewTenantRegistrationData): Promise<AuthResponse> {
    const response = await apiService.post<any>('/auth/register-new-tenant', registrationData)

    // 登録成功時は自動ログインしない（初期パスワードでログインが必要）
    return {
      message: response.message || '企業登録に成功しました',
      token: null, // 自動ログインしない
      user: response.user,
      requiresPasswordChange: false,
      firstLogin: false,
    }
  }

  // ログイン
  async login(loginData: LoginData): Promise<AuthResponse | MfaLoginResponse> {
    const response = await apiService.post<any>('/auth/login', loginData)

    // MFAが必要な場合
    if (response.requiresMfa) {
      return {
        requiresMfa: true,
        mfaSessionToken: response.mfaSessionToken,
        message: response.message || '2段階認証が必要です',
      } as MfaLoginResponse
    }

    // 初回ログインでも通常のtokenを使用（limitedTokenは廃止）
    const token = response.token

    if (!token) {
      throw new Error('認証トークンが取得できませんでした')
    }

    // トークンとユーザー情報をローカルストレージに保存
    localStorage.setItem('authToken', token)
    localStorage.setItem('user', JSON.stringify(response.user))

    // APIサービスにトークンを設定
    apiService.setAuthToken(token)

    // AuthResponseフォーマットに変換して返却
    return {
      message: response.message || 'ログインに成功しました',
      token,
      user: response.user,
      requiresPasswordChange: response.requirePasswordChange || false,
      firstLogin: response.firstLogin || false, // 初回ログインフラグ追加
    }
  }

  // ログアウト
  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout')
    } catch (error) {
      // ログアウトAPIが失敗してもローカル処理は実行
      console.error('Logout API failed:', error)
    } finally {
      // ローカルストレージクリア
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')

      // APIサービスからトークン削除
      apiService.clearAuthToken()
    }
  }

  // プロフィール取得
  async getProfile(): Promise<{ user: User }> {
    return await apiService.get<{ user: User }>('/auth/profile')
  }

  // プロフィール更新
  async updateProfile(profileData: Partial<User>): Promise<{ message: string; user: User }> {
    return await apiService.put<{ message: string; user: User }>('/auth/profile', profileData)
  }

  // パスワード変更
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiService.post<{ message: string }>('/auth/change-password', {
        currentPassword,
        newPassword,
      })

      return {
        message: response.message || 'パスワードが正常に変更されました',
        success: true,
      }
    } catch (error: any) {
      return {
        message: error.response?.data?.error || 'パスワード変更に失敗しました',
        success: false,
      }
    }
  }

  // トークン検証
  async verifyToken(): Promise<{ valid: boolean; user: User }> {
    return await apiService.get<{ valid: boolean; user: User }>('/auth/verify')
  }

  // ローカルストレージからトークン取得
  getStoredToken(): string | null {
    return localStorage.getItem('authToken')
  }

  // ローカルストレージからユーザー情報取得
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        return JSON.parse(userStr) as User
      } catch (error) {
        console.error('Failed to parse stored user:', error)
        localStorage.removeItem('user')
      }
    }
    return null
  }

  // 認証状態チェック
  isAuthenticated(): boolean {
    const token = this.getStoredToken()
    const user = this.getStoredUser()
    return !!(token && user)
  }

  // 権限チェック
  hasRole(user: User | null, allowedRoles: string[]): boolean {
    if (!user) return false
    return allowedRoles.includes(user.role)
  }

  // APIサービスにトークンを設定
  setToken(token: string): void {
    apiService.setAuthToken(token)
  }

  // スーパー管理者権限チェック
  isSuperAdmin(user: User | null): boolean {
    return this.hasRole(user, ['SUPER_ADMIN'])
  }

  // 管理者権限チェック（管理者以上）
  isAdmin(user: User | null): boolean {
    return this.hasRole(user, ['CORPORATE_ADMIN', 'SUPER_ADMIN'])
  }

  // 管理者以上権限チェック（旧HR_MANAGER以上）
  isCorporateAdminOrAbove(user: User | null): boolean {
    return this.hasRole(user, ['CORPORATE_ADMIN', 'SUPER_ADMIN'])
  }

  // パスワードリセット
  async resetPassword(email: string): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiService.post<{ message: string; success: boolean }>(
        '/auth/reset-password',
        {
          email,
        }
      )

      return {
        message: response.message || 'パスワードリセットメールを送信しました',
        success: response.success || true,
      }
    } catch (error: any) {
      return {
        message: error.response?.data?.error || 'パスワードリセットに失敗しました',
        success: false,
      }
    }
  }

  // 後方互換性のための旧メソッド名のエイリアス
  isHROrAdmin(user: User | null): boolean {
    return this.isCorporateAdminOrAbove(user)
  }

  // ==========================================
  // 招待トークン関連（Magic Link方式）
  // ==========================================

  // 招待トークン検証
  async verifyInvitation(token: string): Promise<{
    valid: boolean
    user?: { name: string; email: string }
    expiresAt?: string
    error?: string
  }> {
    try {
      const response = await apiService.get<{
        valid: boolean
        user?: { name: string; email: string }
        expiresAt?: string
        error?: string
      }>(`/auth/invite/verify/${token}`)
      return response
    } catch (error: any) {
      return {
        valid: false,
        error: error.response?.data?.error || '招待リンクの検証に失敗しました',
      }
    }
  }

  // 招待受諾（パスワード設定）
  async acceptInvitation(token: string, newPassword: string): Promise<AuthResponse> {
    const response = await apiService.post<any>('/auth/invite/accept', {
      token,
      newPassword,
    })

    // トークンとユーザー情報をローカルストレージに保存
    if (response.token) {
      localStorage.setItem('authToken', response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      apiService.setAuthToken(response.token)
    }

    return {
      message: response.message || 'アカウント設定が完了しました',
      token: response.token,
      user: response.user,
      requiresPasswordChange: false,
      firstLogin: false,
    }
  }

  // 招待再送信（管理者用）
  async resendInvitation(
    userId: string
  ): Promise<{ message: string; success: boolean; expiresAt?: string }> {
    try {
      const response = await apiService.post<{
        message: string
        success: boolean
        expiresAt?: string
      }>('/auth/invite/resend', { userId })
      return response
    } catch (error: any) {
      return {
        message: error.response?.data?.error || '招待の再送信に失敗しました',
        success: false,
      }
    }
  }

  // ユーザー言語設定更新
  async updateUserLanguage(userId: string, language: Language): Promise<User> {
    const response = await apiService.put<User>(`/users/${userId}`, {
      preferredLanguage: language,
    })
    return response
  }

  // ユーザーのアクセス可能な機能を取得（ロール機能管理で設定された機能）
  async getAccessibleFeatures(userId: string): Promise<AccessibleFeature[]> {
    try {
      const response = await apiService.get<AccessibleFeature[]>(
        `/role-features/user/${userId}/accessible-features`
      )
      return response
    } catch (error) {
      console.error('Failed to fetch accessible features:', error)
      return []
    }
  }

  // ==========================================
  // MFA（多要素認証）関連
  // ==========================================

  // MFA状態取得
  async getMfaStatus(): Promise<MfaStatus> {
    return await apiService.get<MfaStatus>('/mfa/status')
  }

  // MFAセットアップ開始（QRコード取得）
  async setupMfa(): Promise<MfaSetupResponse> {
    return await apiService.post<MfaSetupResponse>('/mfa/setup', {})
  }

  // MFA有効化（コード検証後）
  async enableMfa(code: string): Promise<MfaEnableResponse> {
    return await apiService.post<MfaEnableResponse>('/mfa/enable', { code })
  }

  // MFA無効化
  async disableMfa(password: string): Promise<{ message: string }> {
    return await apiService.post<{ message: string }>('/mfa/disable', { password })
  }

  // MFA検証（ログイン時）
  async verifyMfa(mfaSessionToken: string, code: string): Promise<MfaVerifyResponse> {
    const response = await apiService.post<MfaVerifyResponse>('/mfa/verify', {
      mfaSessionToken,
      code,
    })

    // トークンとユーザー情報をローカルストレージに保存
    if (response.token) {
      localStorage.setItem('authToken', response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      apiService.setAuthToken(response.token)
    }

    return response
  }

  // バックアップコードで認証（ログイン時）
  async verifyMfaBackup(mfaSessionToken: string, backupCode: string): Promise<MfaVerifyResponse> {
    const response = await apiService.post<MfaVerifyResponse>('/mfa/verify-backup', {
      mfaSessionToken,
      backupCode,
    })

    // トークンとユーザー情報をローカルストレージに保存
    if (response.token) {
      localStorage.setItem('authToken', response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      apiService.setAuthToken(response.token)
    }

    return response
  }

  // バックアップコード再生成
  async regenerateBackupCodes(code: string): Promise<MfaEnableResponse> {
    return await apiService.post<MfaEnableResponse>('/mfa/backup-codes', { code })
  }

  // MFAレスポンス判定ヘルパー
  isMfaRequired(response: AuthResponse | MfaLoginResponse): response is MfaLoginResponse {
    return 'requiresMfa' in response && response.requiresMfa === true
  }
}

export const authService = new AuthService()
